import { ContactType, RoleId, SiteMapPage } from '../types';

export type ResourceId = string;
export type ResourceType = 'page' | 'role' | 'contactType';
export type Permission = { resourceId: ResourceId; type?: ResourceType };
export type RolePermissions = { includesRoles?: RoleId[]; permissions?: Permission[] };

// For non-wildcard page permissions, use SiteMapPage enum values.
// Navbar paths use a different format than SiteMapPage - always use SiteMapPage format here.

// RolePermissions structure:
//  includesRoles: Inherits ALL permissions from the listed roles (inheritance is recursive)
//  permissions: Direct permissions granted to this role.
//    type: 'page' = access to a route (use siteMapPage.ts format, important!!!)
//    type: 'role' = ability to assign this role to other users
//    type: 'contactType' = ability to create/edit contacts with this contact type

export const rolesPermissions: Record<RoleId, RolePermissions> = {
    // Base roles
    unauthenticatedUser: {
        permissions: [
            { resourceId: SiteMapPage.home, type: 'page' },
            { resourceId: SiteMapPage.changePassword, type: 'page' },
            { resourceId: SiteMapPage.resetPassword, type: 'page' },
            { resourceId: SiteMapPage.signIn, type: 'page' },
            { resourceId: '/errors/*', type: 'page' },
        ],
    },
    authenticatedUser: {
        includesRoles: ['unauthenticatedUser'],
        permissions: [
            { resourceId: '/account/*', type: 'page' },
            { resourceId: '/news/*', type: 'page' },
            { resourceId: '/help/*', type: 'page' },
            { resourceId: '/notifications/*', type: 'page' },
        ],
    },

    // Reseller returns roles
    resellerViewer: {
        includesRoles: ['authenticatedUser'],
        permissions: [
            { resourceId: SiteMapPage.listContacts, type: 'page' },
            { resourceId: SiteMapPage.listReturnOrders, type: 'page' },
            { resourceId: SiteMapPage.viewReturnOrder, type: 'page' },
        ],
    },
    resellerCreator: {
        includesRoles: ['resellerViewer'],
        permissions: [
            { resourceId: '/returns/*', type: 'page' },
            { resourceId: '/reseller/*', type: 'page' },
            { resourceId: ContactType.returns, type: 'contactType' },
            { resourceId: ContactType.creditMemos, type: 'contactType' },
        ],
    },
    resellerAdmin: {
        includesRoles: ['resellerCreator', 'claimCreator'],
        permissions: [
            { resourceId: '/admin/*', type: 'page' },
            { resourceId: '/certificates/*', type: 'page' },
            { resourceId: 'resellerAdmin', type: 'role' },
            { resourceId: 'resellerCreator', type: 'role' },
            { resourceId: 'resellerViewer', type: 'role' },
            { resourceId: 'claimCreator', type: 'role' },
            { resourceId: 'claimViewer', type: 'role' },
            { resourceId: 'pdfClaimAdmin', type: 'role' },
            { resourceId: 'pdfClaimCreator', type: 'role' },
        ],
    },

    // Claim roles
    claimViewer: {
        includesRoles: ['authenticatedUser'],
        permissions: [{ resourceId: SiteMapPage.viewClaims, type: 'page' }],
    },
    claimCreator: {
        includesRoles: ['claimViewer'],
        permissions: [{ resourceId: '/claims/*', type: 'page' }],
    },

    // PDF claim roles
    pdfClaimCreator: {
        includesRoles: ['claimCreator'],
    },
    pdfClaimAdmin: {
        includesRoles: ['pdfClaimCreator'],
        permissions: [
            { resourceId: 'pdfClaimAdmin', type: 'role' },
            { resourceId: 'pdfClaimCreator', type: 'role' },
        ],
    },

    // Purchase order roles
    poViewer: {
        includesRoles: ['authenticatedUser'],
        permissions: [
            { resourceId: SiteMapPage.viewPurchaseOrder, type: 'page' },
            { resourceId: SiteMapPage.listPurchaseOrders, type: 'page' },
            { resourceId: SiteMapPage.listContacts, type: 'page' },
            // Viewers get the extraction dashboard but not the draft review page, which poEditor adds.
            { resourceId: SiteMapPage.listExtractions, type: 'page' },
        ],
    },
    poAdmin: {
        includesRoles: ['poViewer', 'poEditor'],
        permissions: [
            { resourceId: '/po-status/*', type: 'page' },
            { resourceId: '/reseller/*', type: 'page' },
            { resourceId: '/admin/*', type: 'page' },
            { resourceId: 'poAdmin', type: 'role' },
            { resourceId: 'poViewer', type: 'role' },
            { resourceId: 'poEditor', type: 'role' },
            { resourceId: ContactType.purchaseOrderReports, type: 'contactType' },
            { resourceId: ContactType.purchaseOrderSubmitter, type: 'contactType' },
        ],
    },
    poEditor: {
        includesRoles: ['poViewer'],
        permissions: [{ resourceId: SiteMapPage.reviewPurchaseOrderExtraction, type: 'page' }],
    },

    // Certificate roles
    certificateAdmin: {
        includesRoles: ['authenticatedUser'],
        permissions: [{ resourceId: '/certificates/*', type: 'page' }],
    },

    // Amazon roles
    amazonCreator: {
        includesRoles: ['claimCreator', 'resellerCreator'],
        permissions: [
            { resourceId: ContactType.purchaseOrderReports, type: 'contactType' },
            { resourceId: ContactType.purchaseOrderSubmitter, type: 'contactType' },
        ],
    },
    amazonAdmin: {
        includesRoles: ['resellerAdmin', 'pdfClaimAdmin', 'certificateAdmin', 'poAdmin'],
        permissions: [
            { resourceId: '/*', type: 'page' },
            { resourceId: 'amazonAdmin', type: 'role' },
            { resourceId: 'amazonCreator', type: 'role' },
            { resourceId: 'certificateAdmin', type: 'role' },
        ],
    },
};
